import React, { useState, useRef } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import { ScanLine, Camera, Upload, Loader, Check, Sprout, PackagePlus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SeedScanner({ user }) {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [savedToBox, setSavedToBox] = useState(false);
  const [savingBox, setSavingBox] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const onSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    setResult(null); setError(''); setSavedToBox(false);
  };

  const scan = async () => {
    if (!selectedImage) return;
    setScanning(true); setError(''); setResult(null); setSavedToBox(false);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(selectedImage);
      });
      const res = await axios.post(`${API}/scanner/seed-packet`, { image: base64 }, { withCredentials: true });
      setResult(res.data);
      if (!res.data.recognized) setError(res.data.message || "That doesn't look like a seed packet — try a clearer photo of the front.");
    } catch (e) {
      setError('Sorry, I had trouble reading that packet. Please try a clearer, well-lit photo.');
    } finally { setScanning(false); }
  };

  const ex = result?.extracted || {};
  const matched = result?.matched_crop;

  const addToGarden = () => {
    if (!matched?.crop_id) return;
    navigate(`/crops/${matched.crop_id}?add=1${ex.variety ? `&variety=${encodeURIComponent(ex.variety)}` : ''}`);
  };

  const addToSeedBox = async () => {
    setSavingBox(true); setError('');
    try {
      await axios.post(`${API}/seeds`, {
        crop_name: matched?.name || ex.crop_name,
        crop_id: matched?.crop_id || null,
        variety: ex.variety || null,
        supplier: ex.brand || null,
        initial_seeds: ex.seed_count || null,
        notes: ex.notes || null,
      }, { withCredentials: true });
      setSavedToBox(true);
    } catch (e) { setError('Could not save to your Seed Box. Please try again.'); }
    finally { setSavingBox(false); }
  };

  const detailRows = result?.recognized ? [
    ['Brand', ex.brand],
    ['Days to maturity', ex.days_to_maturity],
    ['Sowing depth', ex.sowing_depth_cm != null ? `${ex.sowing_depth_cm} cm` : null],
    ['Spacing', ex.spacing_cm != null ? `${ex.spacing_cm} cm` : null],
    ['Sow', ex.sow_months],
    ['Sun', ex.sun],
    ['Seeds in packet', ex.seed_count],
  ].filter(([, v]) => v != null && v !== '') : [];

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#1A4331] flex items-center justify-center flex-shrink-0">
            <ScanLine className="w-6 h-6 text-[#B4D330]" />
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Seed Packet Scanner</h1>
            <p className="text-base text-[#5C665C] mt-1">Snap a packet — I'll read the variety and sowing details for you</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#1A4331]/5 shadow-sm">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={onSelect} className="hidden" data-testid="seed-scan-image-input" />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onSelect} className="hidden" data-testid="seed-scan-camera-input" />

          {!previewUrl ? (
            <div data-testid="seed-scanner-upload-area" className="border-2 border-dashed border-[#8EAA97] rounded-2xl p-10 sm:p-12 text-center">
              <ScanLine className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
              <p className="text-[#2D241E] font-semibold mb-1">Photograph the front of the seed packet</p>
              <p className="text-sm text-[#5C665C] mb-6">Make sure the crop name and sowing text are in frame and readable</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <button onClick={() => cameraInputRef.current?.click()} data-testid="seed-scan-camera-button" className="flex-1 bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full transition-colors flex items-center justify-center gap-2">
                  <Camera className="w-5 h-5" /> Take a photo
                </button>
                <button onClick={() => fileInputRef.current?.click()} data-testid="seed-scan-upload-button" className="flex-1 bg-white border border-[#8EAA97] text-[#1A4331] hover:bg-[#F0F2EB] font-semibold py-3 px-6 rounded-full transition-colors flex items-center justify-center gap-2">
                  <Upload className="w-5 h-5" /> Upload photo
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden">
                <img src={previewUrl} alt="Seed packet preview" className="w-full h-auto max-h-80 object-contain bg-[#F0F2EB]" data-testid="seed-scan-image-preview" />
              </div>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => cameraInputRef.current?.click()} className="text-[#1A4331] font-semibold hover:text-[#255C44] flex items-center gap-2"><Camera className="w-4 h-4" /> Retake</button>
                <button onClick={() => fileInputRef.current?.click()} className="text-[#1A4331] font-semibold hover:text-[#255C44] flex items-center gap-2"><Upload className="w-4 h-4" /> Choose another</button>
              </div>
              <button onClick={scan} disabled={scanning} data-testid="seed-scan-analyze-button" className="w-full bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-4 px-8 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {scanning ? (<><Loader className="w-5 h-5 animate-spin" /> Reading the packet…</>) : 'Scan this packet'}
              </button>
            </div>
          )}
        </div>

        {error && <div data-testid="seed-scan-error" className="bg-[#C66B4A]/10 border border-[#C66B4A]/25 text-[#8A3F28] rounded-2xl px-5 py-4 text-sm">{error}</div>}

        {result?.recognized && (
          <div data-testid="seed-scan-result" className="rounded-3xl overflow-hidden border border-[#1A4331]/10">
            <div className="relative overflow-hidden bg-gradient-to-br from-[#1A4331] to-[#0F2D1F] text-[#FAF6EC] p-6">
              <div className="absolute -top-14 -right-10 w-48 h-48 bg-[#B4D330]/15 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-[#B4D330]">Packet read</p>
                <h2 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="seed-scan-crop-name">{ex.crop_name}{ex.variety ? ` · ${ex.variety}` : ''}</h2>
                {matched ? (
                  <span data-testid="seed-scan-matched-badge" className="inline-flex items-center gap-1.5 mt-2 bg-white/15 rounded-full px-3 py-1 text-xs font-semibold"><Check className="w-3.5 h-3.5 text-[#B4D330]" /> Matched to {matched.name} in our handbook</span>
                ) : (
                  <span className="inline-block mt-2 bg-white/10 rounded-full px-3 py-1 text-xs text-[#FAF6EC]/80">No handbook match — you can still save it to your Seed Box</span>
                )}
              </div>
            </div>

            <div className="bg-[#FAF6EC] p-6 space-y-5">
              {detailRows.length > 0 && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {detailRows.map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <span className="text-xs font-bold tracking-wider uppercase text-[#7A6B57]">{k}</span>
                      <span className="text-[#1A4331] font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {ex.notes && <p className="text-sm text-[#5C665C] italic border-l-2 border-[#8EAA97] pl-3">{ex.notes}</p>}

              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                <button onClick={addToGarden} disabled={!matched?.crop_id} data-testid="seed-scan-add-garden" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3.5 px-6 rounded-full transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                  <Sprout className="w-5 h-5" /> Add to my garden
                </button>
                {savedToBox ? (
                  <button onClick={() => navigate('/seed-box')} data-testid="seed-scan-saved" className="bg-[#6B9F4A] text-white font-semibold py-3.5 px-6 rounded-full flex items-center justify-center gap-2">
                    <Check className="w-5 h-5" /> Saved · View Seed Box
                  </button>
                ) : (
                  <button onClick={addToSeedBox} disabled={savingBox} data-testid="seed-scan-add-seedbox" className="bg-white border-2 border-[#1A4331] text-[#1A4331] hover:bg-[#1A4331]/5 font-semibold py-3.5 px-6 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    <PackagePlus className="w-5 h-5" /> {savingBox ? 'Saving…' : 'Add to Seed Box'}
                  </button>
                )}
              </div>
              {!matched?.crop_id && <p className="text-xs text-[#7A6B57] text-center">We couldn't match this crop to the handbook, so garden planting is unavailable — but it's saved in your Seed Box.</p>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
